<template>
  <v-card class="mt-3 rounded-card card-shadow">
    <v-card-text class="text--primary">
      <v-row dense>
        <v-col cols="12" v-if="isMoedaEstrangeira">
          <px-moeda-chip :moeda="moeda"/>
        </v-col>
        <v-col cols="12" sm="4">
          <template v-if="isMoedaEstrangeira">
            <v-row dense no-gutters class="body-1">
              <v-col cols="6">
                Produtos:
              </v-col>
              <v-col cols="6" class="text-right">
                {{
                  compra.totalProdutos |
                    currency(simboloMoeda, casaDecimal, configuracaoFormatacaoMoeda)
                }}
              </v-col>
              <v-col cols="6">
                Frete:
              </v-col>
              <v-col cols="6" class="text-right">
                {{
                  compra.frete.totalTransporte |
                    currency(simboloMoeda, 2, configuracaoFormatacaoMoeda)
                }}
              </v-col>
              <v-col cols="6" class="font-weight-bold">
                Total:
              </v-col>
              <v-col cols="6" class="text-right">
                  <span class="text--primary--color font-weight-bold">
                    {{
                      compra.total |
                        currency(simboloMoeda, casaDecimal, configuracaoFormatacaoMoeda)
                    }}
                  </span>
              </v-col>
              <template v-if="isMoedaEstrangeira && compra.saving">
                <v-col cols="6" class="font-weight-bold">
                  Saving:
                </v-col>
                <v-col cols="6" class="text-right">
                      <span class="text--success--color font-weight-bold">
                        {{
                          compra.saving |
                            currency(simboloMoeda, 2, configuracaoFormatacaoMoeda)
                        }}
                      </span>
                </v-col>
              </template>
            </v-row>
          </template>
        </v-col>
        <v-col cols="12" sm="4"></v-col>
        <v-col cols="12" sm="4">
          <v-row dense no-gutters class="body-1">
            <v-col cols="6">
              Produtos:
            </v-col>
            <v-col cols="6" class="text-right">
              {{ compra.valorMoedaConvertido.totalProdutos | currency('R$', casaDecimal) }}
            </v-col>
            <v-col cols="6">
              Frete:
            </v-col>
            <v-col cols="6" class="text-right">
              {{ compra.frete.totalTransporteConvertido | currency }}
            </v-col>
            <v-col cols="6" class="font-weight-bold">
              Total:
            </v-col>
            <v-col cols="6" class="text-right">
                  <span class="text--primary--color font-weight-bold">
                    {{ compra.valorMoedaConvertido.total | currency('R$', casaDecimal) }}
                  </span>
            </v-col>
            <template v-if="compra.saving">
              <v-col cols="6" class="font-weight-bold">
                Saving:
              </v-col>
              <v-col cols="6" class="text-right">
                      <span class="text--success--color font-weight-bold">
                        {{ compra.valorMoedaConvertido.saving | currency }}
                      </span>
              </v-col>
            </template>
          </v-row>
        </v-col>
      </v-row>
      <v-data-table
        show-expand
        hide-default-footer
        disable-pagination
        :headers="headers"
        :items="sortedProdutos"
        item-key="id"
        class="elevation-0 mt-3"
      >
        <template v-slot:item.descricao="{ item }">
          <px-entrega-menu v-if="hasControleEntrega && !isRecusadoCancelado"
                           :item="item"
                           :has-role-gestor-entrega="hasRoleGestorEntrega"
                           @change-data-entrega="updatePrevisaoEntrega"
                           @change-entrega="finalizarEntrega">
            <template v-slot:activator="{ on }">
              <px-situacao-entrega-compra-icon v-on="on"
                                               :situacao="item.situacaoEntrega"/>
            </template>
          </px-entrega-menu>
          <px-span-tooltip v-if="checkCodigo(item.produto)"
                           top
                           :text="labelCodigo(item.produto)"
                           tooltip="Código interno"/>
          {{ item.produto.descricao }}&nbsp;
          <px-historico-preco-produto small
                                      :produto="item.produto"
                                      :unidade-medida="item.unidadeMedida"/>
          <px-anexo v-if="item.arquivos && item.arquivos.length"
                    small read-only dot
                    v-model="item.arquivos"
                    download-base-path="files/download-comprador">
            <template v-slot:activator="{ on }">
              <v-btn small icon
                     v-on="on">
                <v-icon small>mdi-paperclip</v-icon>
              </v-btn>
            </template>
          </px-anexo>
        </template>
        <template v-slot:item.quantidade="{ item }">
          <span>
            {{ item.quantidade | number(getCurrencyFormat(item.produto.casasDecimais)) }}&nbsp;
          {{ item.unidadeMedida.descricao }}
          </span>
        </template>
        <template v-slot:item.precoUnitario="{ item }">
          <div>
            {{
              item.valorMoedaConvertido.precoUnitario
                | currency('R$', item.produto.casasDecimais)
            }}
            <div class="caption" v-if="isMoedaEstrangeira">
              {{
                item.precoUnitario
                  | currency(simboloMoeda,
                  item.produto.casasDecimais,
                  configuracaoFormatacaoMoeda)
              }}
            </div>
          </div>
        </template>
        <template v-slot:item.icms="{ item }">
          <px-imposto
            :imposto="item.impostos"
            show-valor-convertido
            tipo="ICMS"
            :show-aplicacao="false"
            :scale="item.produto.casasDecimais"/>
          <div v-if="isMoedaEstrangeira">
            <px-imposto
              class="caption"
              :imposto="item.impostos"
              :simbolo-moeda="simboloMoeda"
              :configuracao-moeda="configuracaoFormatacaoMoeda"
              tipo="ICMS"
              :show-aplicacao="false"
              :scale="item.produto.casasDecimais"/>
          </div>
        </template>
        <template v-slot:item.ipi="{ item }">
          <px-imposto
            show-valor-convertido
            :imposto="item.impostos"
            tipo="IPI"
            :show-aplicacao="false"
            :scale="item.produto.casasDecimais"/>
          <div v-if="isMoedaEstrangeira">
            <px-imposto
              class="caption"
              :imposto="item.impostos"
              :simbolo-moeda="simboloMoeda"
              :configuracao-moeda="configuracaoFormatacaoMoeda"
              tipo="IPI"
              :show-aplicacao="false"
              :scale="item.produto.casasDecimais"/>
          </div>
        </template>
        <template v-slot:item.total="{ item }">
          <div>
            {{ item.valorMoedaConvertido.total | currency('R$', item.produto.casasDecimais) }}
            <div class="caption" v-if="isMoedaEstrangeira">
              {{
                item.total
                  | currency(simboloMoeda,
                  item.produto.casasDecimais,
                  configuracaoFormatacaoMoeda)
              }}
            </div>
          </div>
          <px-situacao-cotacao-produto :situacao="item.situacao"/>
        </template>
        <template v-if="isFromCotacao" v-slot:item.options="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <px-resposta-detalhes-dialog-view
                :cotacao-id="item.respostaCotacaoProduto.respostaCotacao.cotacao.id"
                :resposta-id="item.respostaCotacaoProduto.respostaCotacao.id"
              />
              <v-btn v-on="on" icon link target="_blank" :to="linkCotacao(item)">
                <v-icon>mdi-open-in-new</v-icon>
              </v-btn>
            </template>
            <span>Ir para a cotação</span>
          </v-tooltip>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="grey lighten-4 expanded-item">
            <v-row dense>
              <v-col cols="12" sm="4">
                <span class="caption font-weight-bold">Observação Externa do produto</span>
                <div>
                  <px-show-large-text :large-text="observacaoExterna(item)"/>
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <span class="caption font-weight-bold">Observação Interna do produto</span>
                <div>
                  <px-show-large-text :large-text="observacaoInterna(item)"/>
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <span class="caption font-weight-bold">{{ labelEspecificacaoProduto }}</span>
                <div>
                  <px-show-large-text :large-text="item.especificacaoProduto"/>
                </div>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="4" v-if="isFromCotacao">
                <span class="caption font-weight-bold">Especificação cotada</span>
                <div>
                  <px-show-large-text :large-text="item.especificacaoProdutoOrigem"/>
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <span class="caption font-weight-bold">Código referência do fornecedor</span>
                <div>
                  {{ item.produto.codigoFornecedor | naoInformado }}
                </div>
              </v-col>
              <v-col cols="12" sm="4" v-if="item.produto.precoTarget">
                <span class="caption font-weight-bold">Preço Target</span>
                <div>
                  {{ item.produto.precoTarget | currency('R$', getCasaDecimalItem(item)) }}
                </div>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="4" v-if="item.centroCusto">
                <span class="caption font-weight-bold">Centro de custo</span>
                <div>
                  {{ centroCusto(item.centroCusto) | truncate(45) }}
                </div>
              </v-col>
              <v-col cols="12" sm="4" v-if="item.projeto">
                <span class="caption font-weight-bold">Projeto</span>
                <div>
                  {{ projeto(item.projeto) | truncate(45) }}
                </div>
              </v-col>
            </v-row>
            <px-campos-integracao v-if="cadastrosAdicionaisItem(item)"
                                  type="compra-produtos-view"
                                  :form-data="cadastrosAdicionaisItem(item)"/>
          </td>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import Vue from 'vue';
import { OrigemCompra, SituacaoCompra, SituacaoEntregaCompra } from 'px-business-components';
import { CasaDecimalUtils } from 'px-components';
import CompraProdutoService from '@/modules/compra/compra-produto-service';
import { sortBy } from 'lodash';
import { mapGetters } from 'vuex';
import { cadastrosAdicionaisToMap } from '@/utils/campos-integracao-utils';
import { getPrecision } from '@/utils/produto-utils';
import ObjectUtils from '@/utils/object-utils';
import moedaMixin from '@/mixins/moeda-mixin';
import RespostaDetalhesDialogView
  from '@/modules/cotacao/resposta/detalhes/RespostaDetalhesDialogView.vue';

export default {
  mixins: [moedaMixin],
  props: {
    compra: Object,
  },
  data() {
    return {
      headers: [
        {
          text: 'Produto',
          sortable: false,
          value: 'descricao',
        },
        {
          text: 'Quantidade',
          value: 'quantidade',
          sortable: false,
        },
        {
          text: 'Preço unitário',
          value: 'precoUnitario',
        },
        {
          text: 'ICMS',
          value: 'icms',
          sortable: false,
        },
        {
          text: 'IPI',
          value: 'ipi',
          sortable: false,
        },
        {
          text: 'Total',
          value: 'total',
        },
        {
          text: '',
          value: 'options',
        },
        {
          text: '',
          value: 'data-table-expand',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('user', ['hasControleEntrega', 'hasRoleGestorEntrega']),
    moeda() {
      return this.compra.moeda;
    },
    cfop() {
      return cfop => `${cfop.codigo} - ${Vue.filter('truncate')(cfop.descricao, 20)}`;
    },
    isFromCotacao() {
      return this.compra.origemCompra === OrigemCompra.keys.COTACAO.key;
    },
    linkCotacao() {
      return item => `/cotacao/${item.respostaCotacaoProduto.respostaCotacao.cotacao.id}/respostas/${item.respostaCotacaoProduto.respostaCotacao.id}`;
    },
    getCurrencyFormat() {
      return item => CasaDecimalUtils.getCurrencyFormat(item);
    },
    casaDecimal() {
      const mappedProdutos = this.compra.produtos.map(produto => produto.produto);
      return CasaDecimalUtils.getCasaDecimal(mappedProdutos);
    },
    getCasaDecimalItem() {
      return item => getPrecision(item.produto);
    },
    checkCodigo() {
      return item => item.codigo;
    },
    labelCodigo() {
      return item => `${item.codigo} -`;
    },
    sortedProdutos() {
      return sortBy(this.compra.produtos, 'produto.descricao');
    },
    cadastrosAdicionaisItem() {
      return (item) => {
        if (!item.cadastrosAdicionais || !item.cadastrosAdicionais.length) {
          return null;
        }
        return cadastrosAdicionaisToMap(item.cadastrosAdicionais);
      };
    },
    observacaoInterna() {
      return (produto) => {
        if (produto.observacaoInterna) {
          return produto.observacaoInterna;
        }
        return produto.produto.observacaoInterna;
      };
    },
    observacaoExterna() {
      return (produto) => {
        if (produto.observacaoExterna) {
          return produto.observacaoExterna;
        }
        return produto.produto.observacaoExterna;
      };
    },
    projeto() {
      return projeto => ObjectUtils.descWithCodigoInterno(projeto);
    },
    centroCusto() {
      return centroCusto => ObjectUtils.descWithCodigoInterno(centroCusto);
    },
    labelEspecificacaoProduto() {
      return this.isFromCotacao ? 'Especificação do fornecedor' : 'Especificação do produto';
    },
    isRecusadoCancelado() {
      return this.compra.situacao === SituacaoCompra.keys.RECUSADA.key
        || this.compra.situacao === SituacaoCompra.keys.CANCELADA.key;
    },
  },
  methods: {
    updatePrevisaoEntrega(item, dataPrevisao) {
      CompraProdutoService.updatePrevisaoEntregaProduto(this.compra.id, item.id, {
        dataPrevisaoEntrega: dataPrevisao,
      }).then(() => {
        this.$toast('Data de entrega atualizada', { color: 'success' });
      });
    },
    finalizarEntrega(item) {
      CompraProdutoService.finalizarEntregaProduto(this.compra.id, item.id).then(() => {
        // eslint-disable-next-line no-param-reassign
        item.situacaoEntrega = SituacaoEntregaCompra.keys.RECEBIDA.key;
        this.$toast('Compra atualizada', { color: 'success' });
      });
    },
  },
  components: {
    pxRespostaDetalhesDialogView: RespostaDetalhesDialogView,
  },
};
</script>
